<template>
  <div class="container-controllercard" :class="{ selected: isSelected }" @click="emit('select', card.id)">
    <div class="container-group">
      <!-- 마이크선택 -->
      <MCSDropdownMic
        v-model="card.microphone"
        :disabled="microphonesStore.isMicOn(selectedMicId)"
        :class="{ highlighted: !isSocketConnected }"
      />

      <!-- 버튼컨트롤 -->
      <button
        class="button-icon-text button-icon-text-big button-full-width"
        :class="[
          microphonesStore.isMicOn(selectedMicId) ? 'button-dim' : 'button-success',
          { 'not-active': !isSocketConnected },
        ]"
        :disabled="!isMicConnected"
        @click="toggleMic()"
      >
        <!-- 대기중 -->
        <template v-if="!microphonesStore.isMicOn(selectedMicId)">
          <IconOn class="icon-medium" />
          사용하기
        </template>

        <!-- 사용중 -->
        <template v-else>
          <span class="status-dot"></span>
          <span>사용중</span>
          <a class="link-error" @click.stop="toggleMic()">사용 중지</a>
        </template>
      </button>
    </div>

    <div class="container-group">
      <!-- 언어선택 -->
      <MCSDropdownLanguage
        :id="card.id"
        v-model:language-selected="card.language"
        v-model:language-presets="languagePresets"
        :show-checkbox="false"
        :class="{ highlighted: !isSocketConnected }"
      />

      <!-- 마이크 감도 조절 -->
      <div class="wrapper-guage" :class="{ 'not-active': !isSocketConnected }">
        <p class="smaller accent">마이크 감도 조절</p>
        <MicInputControl
          v-model:volume-ratio="micSensitivityById[selectedMicId]"
          :effect="microphonesStore.micEffectById[selectedMicId]?.outerRadius"
          :max="20"
        />
      </div>
    </div>

    <div class="container-group">
      <!-- 닉네임 입력 -->
      <input
        v-model="card.nickname"
        type="text"
        :class="{ highlighted: !isSocketConnected }"
        placeholder="닉네임"
        @keyup.enter="changeNickname()"
      />
      <!-- 변경 -->
      <button class="button-secondary m-btn" :disabled="!isSocketConnected" @click.stop="changeNickname()">
        닉네임 변환
      </button>
    </div>

    <div class="container-group">
      <!-- 연결 전 -->
      <template v-if="!isSocketConnected">
        <button class="button-primary m-btn" :disabled="isSocketConnected || !isValidForm" @click.stop="connectSocket">
          연결하기
        </button>
      </template>

      <!-- 연결 후 -->
      <template v-else>
        <div class="status-message">
          <span class="status-dot"></span>
          <p>연결됨</p>
          <a @click.stop="disconnectSocket()">연결 끊기</a>
        </div>
      </template>

      <!-- 에러 메시지 -->
      <p v-if="socketStatusMessage" class="error-message">
        <InfoIcon class="icon-error" />
        {{ socketStatusMessage }}
      </p>
    </div>

    <!--삭제버튼-->
    <button
      class="button-glass-dark button-icon button-icon-small button-rounded delete-card-button"
      @click.stop="deleteCard"
    >
      <IconTrashcan class="icon-white" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { isEmpty } from 'lodash';
import { storeToRefs } from 'pinia';

import api from '@/api';
import { createMcsSubUser } from '@/api/apiService';
import MicInputControl from '@/components/domains/chat/common/MicInputControl.vue';
import InfoIcon from '@/components/icons/IconInfo.vue';
import IconOn from '@/components/icons/IconOn.vue';
import IconTrashcan from '@/components/icons/IconTrashcan.vue';
import MCSDropdownLanguage from '@/components/shared/composite/MCSDropdownLanguage.vue';
import MCSDropdownMic from '@/components/shared/composite/MCSDropdownMic.vue';
import { useCallStore } from '@/stores/call';
import { useMicrophonesStore } from '@/stores/microphones';
import { useSocketStore } from '@/stores/socket';
import { useSocketsStore } from '@/stores/sockets';
import { useToastStore } from '@/stores/toast';
import { IMcbCard, Language } from '@/types/components';
import { IResponseMcsSubUser } from '@/types/response';
import { ConnectionEntry } from '@/types/socket';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import { stringToBase64UrlSafe } from '@/utils/encryptor';

const card = defineModel<IMcbCard>({
  required: true,
  default: () => ({
    id: '',
    userId: '',
    nickname: '',
    microphone: {},
    language: {},
  }),
});

withDefaults(
  defineProps<{
    cards: IMcbCard[];
    userPresetList: IResponseMcsSubUser[];
    isSelected: boolean;
  }>(),
  {
    cards: () => [],
    userPresetList: () => [],
    isSelected: false,
  }
);

const emit = defineEmits<{
  select: [id: string];
  delete: [];
}>();

const microphonesStore = useMicrophonesStore();
const callStore = useCallStore();

const { micSensitivityById } = storeToRefs(microphonesStore);

const route = useRoute();

const socketStore = useSocketStore();
const socketsStore = useSocketsStore();
const toastStore = useToastStore();

const languagePresets = ref<Language[]>([]); // 언어 프리셋
const socketStatusMessage = ref<string>(''); // 소켓 상태 메시지

const shouldRestartMic = ref<boolean>(false);

const isValidForm = computed<boolean>(() => {
  const { language = {}, microphone = {}, nickname = '' } = card.value;
  return !isEmpty(language) && !isEmpty(microphone) && !!nickname;
});

// 선택한 마이크 ID
const selectedMicId = computed<string>(() => {
  const { microphone: { id = '' } = { id: '' } } = card.value;
  return id;
});

// 마이크 연결 여부
const isMicConnected = computed<boolean>(() => {
  const { isConnected = false } = microphonesStore.microphones.find(({ id }) => selectedMicId.value === id) || {
    isConnected: false,
  };
  return isConnected;
});

const currentSocket = computed<ConnectionEntry>(() => {
  return socketsStore.connections.get(card.value.id) || ({} as ConnectionEntry);
});

const socketState = computed<string>(() => {
  return currentSocket.value.state ?? '';
});

// 소켓 연결 여부
const isSocketConnected = computed<boolean>(() => {
  return (
    socketState.value === 'OPEN' && !!currentSocket.value.ws && currentSocket.value.ws.readyState === WebSocket.OPEN
  );
});

// 소켓 연결 여부
const socketConnectionResponse = computed<number>(() => {
  return currentSocket.value.connectionResponse ?? -1;
});

// 카드 삭제
const deleteCard = async () => {
  if (isSocketConnected.value) {
    await disconnectSocket();
  }
  if (microphonesStore.isMicOn(selectedMicId.value)) {
    await microphonesStore.stopMics([selectedMicId.value]);
  }
  emit('delete');
};

const handleMicEvent = (data) => {
  if (!isSocketConnected.value || !navigator.onLine) {
    onOffline();
  } else {
    const audioBytes = new Float32Array(data);
    socketsStore.sendMessageAudioStreamChunk(card.value.id, new Uint8Array(audioBytes.buffer), 'as');
  }
};

// 마이크 on/off
const toggleMic = (onlyThis: boolean = false) => {
  if (!isSocketConnected.value) {
    onOffline();
    microphonesStore.stopMics([selectedMicId.value]);
    socketsStore.sendMessageAudioStreamEnd(card.value.id);
    return;
  }

  if (!microphonesStore.isMicOn(selectedMicId.value)) {
    socketsStore.sendMessageAudioStreamStart(card.value.id);
    microphonesStore.setMicEventById(selectedMicId.value, handleMicEvent);
    microphonesStore.startMics([selectedMicId.value]);
    shouldRestartMic.value = true;
    if (onlyThis) {
      toastStore.info(`${card.value.microphone.label}만 사용합니다.`);
    } else {
      toastStore.info(`${card.value.microphone.label} 사용을 시작합니다.`);
    }
  } else {
    microphonesStore.stopMics([selectedMicId.value]);
    socketsStore.sendMessageAudioStreamEnd(card.value.id);
    shouldRestartMic.value = false;
  }
};

const setSocketStatusMessage = (): boolean => {
  const { loginId = '' } = JSON.parse(localStorage.getItem('userInfo') || '') || {
    loginId: '',
  };
  let message = '';

  if (!navigator.onLine) {
    message = '연결 중인 네크워크가 없습니다.';
  } else if (card.value.userId === loginId) {
    message = '현재 로그인 중인 아이디입니다.';
  }

  return !!(socketStatusMessage.value = message);
};

const requestUpdateSubUser = async () => {
  card.value.nickname = card.value.nickname.trim();

  const code = (route.params.code || '').toString();
  const res = await createMcsSubUser(code, {
    nickname: stringToBase64UrlSafe(card.value.nickname),
  });
  card.value.userId = res.data?.loginId;
};

/**
 * @desc 소켓 활성화
 * */
const connectSocket = async () => {
  if (!isSocketConnected.value && setSocketStatusMessage()) {
    return;
  }
  await requestUpdateSubUser();

  await api
    .post(
      '/api/partner/auth/subUser',
      { nickname: stringToBase64UrlSafe(card.value.nickname) },
      {
        headers: {
          Userid: card.value.userId,
        },
      }
    )
    .then(async ({ data }) => {
      if (!data.token || !data.userId) {
        throw new Error();
      }
      socketsStore.setConnectInfo(card.value.id, {
        userId: data.userId,
        isRegister: '0',
        isTssOn: '0',
        voiceId: '0',
        chatId: socketStore.selectedChatId,
        languageCode: card.value.language.code,
        nickname: stringToBase64UrlSafe(card.value.nickname),
        isPartnerPerson: '0',
        longVoice: '0',
        code: route.params.code.toString(),
        token: `Bearer ${data.token}`,
        evictMySelf: '1',
        callType: callStore.qrCodeInfo.callType,
        connectionType: 'GENERAL',
        agent: JSON.stringify(getBrowserAndDeviceInfo()),
      });
      await socketsStore.setupWebSocket(card.value.id);
    })
    .catch((error) => {
      let messageSocketFail = '';
      if (error.errorCode) {
        switch (error.errorCode) {
          case 1004:
            messageSocketFail = '이 파트너사에는 등록되지 않은 아이디입니다.';
            break;
          default:
            messageSocketFail = '연결을 실패했습니다.';
            break;
        }
      }
      socketStatusMessage.value = messageSocketFail;
      toastStore.error(messageSocketFail);
    });
};

/**
 * @desc 소켓 재연결
 * */
const reconnectSocket = async () => {
  if (socketState.value === 'CLOSED') {
    await connectSocket();
  } else {
    clearSocketInterval();
  }
};
const { pause: clearSocketInterval, resume: startSocketInterval } = useIntervalFn(reconnectSocket, 1000);

/**
 * @desc 소켓 비활성화
 * */
const disconnectSocket = async (isForce: boolean = false) => {
  socketsStore.close(card.value.id, isForce);
  if (microphonesStore.isMicOn(selectedMicId.value)) {
    toggleMic();
  }
};

const changeNickname = async (person?: IResponseMcsSubUser, isAll = false) => {
  socketStatusMessage.value = '';

  if (!card.value.nickname) {
    return;
  }

  if (socketsStore.connections.has(card.value.id) && isSocketConnected.value) {
    socketsStore.sendMessageCn(card.value.id, {
      nickname: stringToBase64UrlSafe(card.value.nickname).toString(),
      isAll,
    });
    toastStore.info(`닉네임이 ${card.value.nickname}(으)로 변경되었습니다.`);
  } else if (person) {
    card.value.userId = person.loginId;
  }
};

// 인터넷 연결 대응 로직
const onOnline = async () => {
  await connectSocket();
  if (shouldRestartMic.value && !microphonesStore.isMicOn(selectedMicId.value)) {
    toggleMic();
  }
};

// 인터넷 연결 대응 로직
const onOffline = async () => {
  if (isSocketConnected.value) {
    await disconnectSocket();
    socketStatusMessage.value = '인터넷 연결이 끊어졌습니다.';
  }
};

onMounted(() => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
});

onUnmounted(async () => {
  window.removeEventListener('online', onOnline);
  window.removeEventListener('offline', onOffline);
});

watch(
  () => socketState.value,
  (state) => {
    switch (state) {
      case 'CLOSE':
        if (socketStatusMessage.value) {
          setTimeout(startSocketInterval, 3000);
        }
        break;
    }
  }
);

watch(
  () => micSensitivityById.value[selectedMicId.value],
  () => {
    microphonesStore.controlGainById(selectedMicId.value);
  }
);

watch(
  () => isMicConnected.value,
  (val) => {
    if (!val && microphonesStore.isMicOn(selectedMicId.value)) {
      toggleMic();
    }
  }
);

// 소켓메시지
watch(
  () => socketConnectionResponse.value,
  async (val) => {
    switch (val) {
      case -1:
      case 0:
      case 1:
      case 2:
        socketStatusMessage.value = '';
        break;
      case 1016: // 잘못된 코드 요청
        socketStatusMessage.value = '잘못된 코드 요청입니다.';
        await disconnectSocket();
        break;
      case 1029: // 사용량 초과
        socketStatusMessage.value = '사용량이 초과되었습니다.';
        await disconnectSocket();
        break;
      case 1030: // 동시 접속시
        socketStatusMessage.value = '동시 접속중 입니다.';
        await disconnectSocket();
        break;
      case 4010: // 인증 실패
        socketStatusMessage.value = '인증을 실패히였습니다.';
        await disconnectSocket();
        break;
      case 1026: // 이미 통역 중일 때 or 동시 접속자 초과
        socketStatusMessage.value = '동시 접속자 초과하였습니다.';
        await disconnectSocket();
        break;
      case 1027: // 이미 종료된 통역일 때
        socketStatusMessage.value = '이미 종료된 통역입니다.';
        await disconnectSocket();
        break;
      case 1023: // 호스트 접속이 안되어있을 때 (FAIR / CONFERENCE)
        socketStatusMessage.value = '호스트 미접속중 입니다.';
        await disconnectSocket();
        break;
      default: // 나머지 에러
        await disconnectSocket();
        break;
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.container-controllercard {
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 55rem;
  height: fit-content;
  flex-shrink: 0;
  padding: 0.5rem;
  gap: 1rem;
  align-items: flex-start;
  background: var(--surface-default);
  border-radius: 12px;
  box-shadow: $shadow-card;
  overflow: hidden;

  &:hover {
    outline: 2px solid var(--border-primary);
  }

  &.selected {
    outline: 2px solid var(--border-primary);
  }
}

.delete-card-button {
  position: absolute;
  bottom: 1rem;
  right: 0.5rem;
  opacity: 0;
  transition: opacity 0.25s ease-in-out;
  z-index: $z-tooltip;
  cursor: pointer;
}

.container-controllercard:hover .delete-card-button {
  opacity: 1;
  pointer-events: auto;
}

.container-group {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 25%;
  flex: 1;
  gap: 0.5rem;
}

.element-smcs {
  flex: 1;
  width: 100%;
  align-items: center;
}

.last {
  max-width: 12rem;
}

.highlighted {
  border: 2px solid var(--border-primary-dim);
  border-radius: 8px;
}

.divider {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.error-message {
  width: 100%;
  display: flex;
  justify-content: left;
  align-items: center;
  color: var(--color-red);
  margin-top: 0.25rem;
  gap: 0.25rem;
}

.status-message {
  height: 3rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.status-dot {
  width: 0.75rem;
  height: 0.75rem;
  background-color: var(--success);
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}

button {
  white-space: nowrap;
}

input {
  max-height: 3rem;
}

.not-active {
  opacity: 0.2;
  pointer-events: none;
}

::v-deep(.contents-wrap) {
  flex: unset;
}
</style>
